import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Sign an idle operator out — a port of `InactivityService` and `wwwroot/js/activity-tracker.js`.
 *
 * The thresholds and the events are the original's: warn at 13 minutes, sign out at 15, watching
 * `mousedown`, `mousemove`, `keydown`, `scroll`, `touchstart` and `click`. They exist because this
 * portal reaches registers that take card payments, and an unattended session is a workstation somebody
 * else can use.
 *
 * **Two departures, and the first is why this is a hook rather than a transcription.**
 *
 * In the original, activity keeps resetting the timers *while the warning is on screen* — the JS
 * listener knows nothing about the dialog. So moving the mouse silently cancels the sign-out while
 * the dialog goes on counting down to zero, and the dialog never closes by itself. It states a
 * deadline that is no longer real and then outlives it. Here the warning suspends activity
 * tracking: once asked, the question has to be answered, and the countdown means what it says.
 *
 * And **"Stay signed in" does not refresh a token.** The original's does, which is one of the two
 * callers that could race a single-use refresh token (see `LocalAuthProvider`). Keeping a session
 * alive is the session layer's job — it renews on a timer of its own, and OIDC renews silently —
 * so this button says only "I am still here", which is the one thing the operator actually knows.
 */

export const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
] as const

export const WARNING_MS = 13 * 60 * 1000
export const LOGOUT_MS = 15 * 60 * 1000

export interface InactivityOptions {
  /** Called when the grace period runs out. */
  onLogout: () => void
  /** False while nobody is signed in: there is no session to protect and no dialog to show. */
  enabled?: boolean
  warnAfterMs?: number
  logoutAfterMs?: number
}

export interface InactivityState {
  warning: boolean
  /** Whole seconds left before the sign-out, counting down while the warning is shown. */
  remainingSeconds: number
  /** Dismiss the warning and start watching for idleness again. */
  stay: () => void
}

export function useInactivity({
  onLogout,
  enabled = true,
  warnAfterMs = WARNING_MS,
  logoutAfterMs = LOGOUT_MS,
}: InactivityOptions): InactivityState {
  const graceMs = logoutAfterMs - warnAfterMs

  const [warning, setWarning] = useState(false)
  const [remainingMs, setRemainingMs] = useState(graceMs)

  // Held in a ref so that a caller passing a fresh closure every render — the ordinary case for
  // `logout` out of a context — does not tear down and restart the countdown on each one.
  const logoutRef = useRef(onLogout)
  logoutRef.current = onLogout

  const stay = useCallback(() => setWarning(false), [])

  // Watch for idleness. The timer is reset in the listener rather than through state, because
  // `mousemove` fires continuously and a re-render per pixel is not a price worth paying for it.
  useEffect(() => {
    if (!enabled || warning) return

    let timer = setTimeout(() => setWarning(true), warnAfterMs)
    const onActivity = () => {
      clearTimeout(timer)
      timer = setTimeout(() => setWarning(true), warnAfterMs)
    }

    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, onActivity, { passive: true })
    }
    return () => {
      clearTimeout(timer)
      for (const event of ACTIVITY_EVENTS) document.removeEventListener(event, onActivity)
    }
  }, [enabled, warning, warnAfterMs])

  // Count down, then sign out. The deadline is a timestamp rather than a decrementing counter, so
  // a tab that was throttled in the background resumes with the truth rather than with however many
  // ticks it was allowed.
  useEffect(() => {
    if (!warning) return

    const deadline = Date.now() + graceMs
    setRemainingMs(graceMs)

    const tick = setInterval(() => {
      const left = deadline - Date.now()
      setRemainingMs(Math.max(0, left))
      if (left <= 0) {
        clearInterval(tick)
        logoutRef.current()
      }
    }, 1000)

    return () => clearInterval(tick)
  }, [warning, graceMs])

  return { warning, remainingSeconds: Math.ceil(remainingMs / 1000), stay }
}
